import * as THREE from 'three'
import fragmentShader from '../shaders/frag-composite.glsl.js'
import { modes } from '../shaderlib/blending.glsl.js'

/**
 * A simple fragment shader that takes some number of input textures and
 * combines them using a provided blending mode.
 * @param {String} blendMode - The blending mode to composite with
 */
export default class CompositeMaterial extends THREE.ShaderMaterial {
  /**
   * The number of textures added to the shader program.
   * @type {Number}
   */
  get #count() {
    return this.uniforms.uTextures.value.length
  }

  // BLENDING MODES ------------------------------------------------------------

  /**
   * Blending mode used to composite multiple textures. See
   * /shaderlib/blending.glsl.js for available options.
   * @type {String}
   */
  #blendMode = 'Add'

  /**
   * A list of all valid blend mode strings.
   * @type {Array<String>}
   */
  static get blendModes() {
    return modes
  }

  /**
   * The currently active blending mode.
   * @type {String}
   */
  get blendMode() {
    return this.#blendMode
  }

  /**
   * Set the blending mode for the shader.
   * @type {String}
   */
  set blendMode(newValue) {
    this.#blendMode = this.#validateBlendMode(newValue)
    this.#updateShader()
  }

  /**
   * Returns a valid blend mode string. If none, or an invalid string is
   * provided, then 'Add' is returned.
   * @param {String} mode - The blending mode to use, as defined in /shaderlib/blending.glsl.js
   * @return {String}
   */
  #validateBlendMode(mode) {
    if (mode && modes.includes(mode)) {
      return mode
    } else if (mode) {
      console.warn(`Invalid blend mode: "${mode}"\n\rUsing previous mode: "${this.#blendMode}"\n\rSee available blend modes with CompositeMaterial.blendModes`)
    }

    return this.#blendMode
  }

  // SHADERS -------------------------------------------------------------------

  /**
   * The vertex shader program for this material. It is a bare-bones shader with
   * the sole purpose of positioning a simple quad.
   * @type {String}
   */
  static #baseVertex = `varying vec2 vUv;

void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`

  /**
   * The default fragment shader for this material. This is replaced once
   * textures are added.
   * @type {String}
   */
  static #baseFragment = `void main() {
    vec3 color = vec3(0.0);
    gl_FragColor = vec4(color, 1.0);
}`

  /**
   * Recreates the fragment shader for the given texture count and blend mode.
   */
  #updateShader() {
    if (this.#count < 1) return
    this.fragmentShader = fragmentShader(this.#count, this.#blendMode)
    this.needsUpdate = true
  }

  // SETUP ---------------------------------------------------------------------

  constructor(blendMode) {
    super({
      uniforms: {
        uTextures: { value: [] }
      },
      vertexShader: CompositeMaterial.#baseVertex,
      fragmentShader: CompositeMaterial.#baseFragment
    })
  }

  // PUBLIC API ----------------------------------------------------------------

  /**
   * Adds a new texture to the internal uTextures array uniform.
   * @param {THREE.Texture} texture
   */
  addTexture(texture) {
    this.uniforms.uTextures.value.push(texture)
    this.#updateShader()
  }

  /**
   * Updates the texture at a specific index.
   * @param {Number} index
   * @param {THREE.Texture} texture
   */
  updateTexture(index, texture) {
    this.uniforms.uTextures.value[index] = texture
  }
}
